import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { SkeletonLine } from './skeletonLine';

const TableWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  border: 1px solid var(--clr-border-grey-100);
  border-radius: 5px;
  background-color: var(--clr-container-bg);
  overflow: hidden;
`;

const Row = styled.div`
  display: flex;
  align-items: stretch;
  width: 100%;

  &:not(:last-child) {
    border-bottom: 1px solid var(--clr-border-grey-100);
  }
`;

/** Matches the timesheet's fixed 140px matter column. */
const MatterCell = styled.div`
  flex: 0 0 140px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
  padding: 10px;
`;

/** Matches the timesheet's narrow 60px date columns. */
const DateCell = styled.div`
  flex: 0 0 60px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding-block: 8px;
`;

interface SkeletonTableProps {
  columns?: number;
  rows?: number;
}

/**
 * Mirrors the timesheet table's shape: a bordered card holding a fixed, wider matter
 * column beside narrow date columns, with the first row previewing the simpler
 * single-line total row that always precedes the matter rows once data loads.
 */
const SkeletonTable = ({ columns = 4, rows = 8 }: SkeletonTableProps) => {
  const { t } = useTranslation();
  const columnCount = columns > 0 ? columns : 1;
  const dateColumnCount = columnCount - 1;

  return (
    <TableWrapper data-testid="skeleton-table" role="status">
      <span className="visually-hidden">{t('common:loading')}</span>
      <Row data-testid="skeleton-table-header">
        <MatterCell>
          <SkeletonLine $height="14px" $width="70%" />
        </MatterCell>
        {Array.from({ length: dateColumnCount }, (_, index) => (
          <DateCell key={`skeleton-table-header-cell-${index}`}>
            <SkeletonLine $height="10px" $width="22px" />
            <SkeletonLine $height="14px" $width="28px" />
          </DateCell>
        ))}
      </Row>
      {Array.from({ length: rows }, (_, rowIndex) => {
        const isTotalRow = rowIndex === 0;
        return (
          <Row key={`skeleton-table-row-${rowIndex}`} data-testid="skeleton-table-row">
            <MatterCell>
              {isTotalRow ? (
                <SkeletonLine $height="13px" $width="60%" />
              ) : (
                <>
                  <SkeletonLine $height="13px" $width="80%" />
                  <SkeletonLine $height="11px" $width="55%" />
                </>
              )}
            </MatterCell>
            {Array.from({ length: dateColumnCount }, (_, colIndex) => (
              <DateCell key={`skeleton-table-cell-${rowIndex}-${colIndex}`}>
                {isTotalRow ? (
                  <SkeletonLine $height="10px" $width="70%" />
                ) : (
                  <>
                    <SkeletonLine $height="10px" $width="26px" />
                    <SkeletonLine $height="20px" $width="20px" $radius="50%" />
                  </>
                )}
              </DateCell>
            ))}
          </Row>
        );
      })}
    </TableWrapper>
  );
};

export default SkeletonTable;
